// Teclado do celular por cima do campo de texto (Assistente IA, relato
// 2026-10-21: "no Android não consigo ver o que estou escrevendo"). Três
// casos:
// 1. Navegador/WebView que encolhe a janela (innerHeight cai) — o layout já
//    acompanha; só avisamos que o teclado está aberto (esconder a barra de
//    abas e ganhar espaço).
// 2. WebView que encolhe só o visualViewport — devolvemos a altura coberta
//    pra empurrar o campo pra cima.
// 3. APK Android 1.19+: o app nativo mede quanto o teclado cobre da WebView
//    e manda o evento "gd-keyboard" (px CSS) — fonte exata, usada sempre
//    que existir (ver MainActivity#reportKeyboard).
// 4. APK antigo que não avisa nada: com um campo focado e nenhuma mudança
//    em 450 ms, usamos uma estimativa (42% da tela).
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";

const isEditable = (el: Element | null) =>
  !!el && (el.tagName === "TEXTAREA" || (el.tagName === "INPUT" && !["checkbox", "radio", "button", "file"].includes((el as HTMLInputElement).type)) || (el as HTMLElement).isContentEditable);

export function useKeyboardInset(active: boolean): { open: boolean; inset: number } {
  const [state, setState] = useState({ open: false, inset: 0 });

  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const vv = window.visualViewport;
    let maxHeight = window.innerHeight;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let baseline = { inner: window.innerHeight, vv: vv?.height ?? window.innerHeight };
    const androidApp = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
    const w = window as Window & { __gdKeyboardBridge?: boolean; __gdKeyboardInset?: number };
    let bridge = w.__gdKeyboardBridge === true;
    let bridgeInset = w.__gdKeyboardInset ?? 0;

    const measure = () => {
      maxHeight = Math.max(maxHeight, window.innerHeight);
      if (bridge) {
        const focused = isEditable(document.activeElement);
        const shrunk = maxHeight - window.innerHeight;
        const next = { open: bridgeInset > 0 || focused || shrunk > 150, inset: bridgeInset };
        setState((prev) => (prev.open === next.open && prev.inset === next.inset ? prev : next));
        return;
      }
      const covered = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0;
      const shrunk = maxHeight - window.innerHeight;
      const focused = isEditable(document.activeElement);
      const inset = covered > 80 ? covered : 0;
      setState((prev) => {
        // Mantém a estimativa enquanto o campo continua focado e nada mudou.
        const keepFallback = prev.inset > 0 && focused && inset === 0 && shrunk < 120 && androidApp;
        const next = { open: focused || inset > 0 || shrunk > 150, inset: keepFallback ? prev.inset : inset };
        return next.open === prev.open && next.inset === prev.inset ? prev : next;
      });
    };

    const onFocusIn = () => {
      baseline = { inner: window.innerHeight, vv: vv?.height ?? window.innerHeight };
      measure();
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (!androidApp || bridge) return;
      fallbackTimer = setTimeout(() => {
        if (!isEditable(document.activeElement)) return;
        const changed = window.innerHeight < baseline.inner - 120 || (vv?.height ?? 0) < baseline.vv - 120;
        if (!changed) setState({ open: true, inset: Math.round(window.innerHeight * 0.42) });
      }, 450);
    };
    const onFocusOut = () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      // O foco pode só estar trocando de campo.
      setTimeout(() => {
        if (!isEditable(document.activeElement)) setState({ open: false, inset: 0 });
        measure();
      }, 60);
    };

    const onBridge = (e: Event) => {
      bridge = true;
      bridgeInset = Math.max(0, Number((e as CustomEvent).detail) || 0);
      if (fallbackTimer) clearTimeout(fallbackTimer);
      measure();
    };
    window.addEventListener("gd-keyboard", onBridge);
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);
    window.addEventListener("resize", measure);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    measure();
    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      window.removeEventListener("gd-keyboard", onBridge);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [active]);

  return active ? state : { open: false, inset: 0 };
}
